"use client";

import clsx from "clsx";
import React from "react";
import Icon from "@/components/library/Icon";

export type ConcurrencyPresetOption = {
  value: number;
  label: string;
  description?: string;
};

type ConcurrencyPresetPickerProps = {
  options: ConcurrencyPresetOption[];
  selectedValue: number;
  onSelect: (value: number) => void;
  /** Compact centered layout used by onboarding (no description column). */
  compact?: boolean;
};

function gridColsClass(count: number): string {
  if (count <= 1) {
    return "grid-cols-1";
  }
  if (count === 2) {
    return clsx("grid-cols-1", "sm:grid-cols-2");
  }
  return clsx("grid-cols-1", "sm:grid-cols-3");
}

/**
 * Card-style preset picker. Uses a native button (not library Button) so we
 * avoid BaseButton's whitespace-nowrap / inline-block / rounded-full defaults
 * that cause overlapping cards in a multi-column grid.
 */
export default function ConcurrencyPresetPicker({
  options,
  selectedValue,
  onSelect,
  compact = false,
}: ConcurrencyPresetPickerProps) {
  return (
    <div
      className={clsx(
        "grid",
        "w-full",
        gridColsClass(options.length),
        "gap-3"
      )}
    >
      {options.map((option) => {
        const isSelected = selectedValue === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onSelect(option.value)}
            className={clsx(
              "relative",
              "flex",
              "w-full",
              "min-w-0",
              "flex-col",
              "items-stretch",
              "rounded-xl",
              "border-2",
              "text-left",
              "transition-all",
              "duration-200",
              // Reserve room for the absolute check badge so labels never collide.
              "px-4",
              "pb-4",
              "pt-4",
              isSelected ? "pr-9" : null,
              isSelected
                ? "border-primary bg-primary/5 shadow-sm"
                : "border-200 bg-panel hover:border-300 hover:shadow-sm"
            )}
          >
            {isSelected && (
              <span
                className={clsx(
                  "absolute",
                  "right-2.5",
                  "top-2.5",
                  "flex",
                  "h-5",
                  "w-5",
                  "shrink-0",
                  "items-center",
                  "justify-center",
                  "rounded-full",
                  "bg-primary",
                  "text-white"
                )}
                aria-hidden
              >
                <Icon icon="check" className={clsx("h-3", "w-3")} />
              </span>
            )}
            {compact ? (
              <span
                className={clsx(
                  "flex",
                  "flex-col",
                  "items-center",
                  "gap-1.5",
                  "text-center"
                )}
              >
                <span
                  className={clsx(
                    "text-2xl",
                    "font-bold",
                    "leading-none",
                    isSelected ? "text-primary" : "text-foreground"
                  )}
                >
                  {option.value}
                </span>
                <span
                  className={clsx(
                    "text-xs",
                    "leading-snug",
                    "text-muted-foreground",
                    "whitespace-normal",
                    "break-words"
                  )}
                >
                  {option.label}
                </span>
              </span>
            ) : (
              <span className={clsx("flex", "min-w-0", "flex-col", "gap-2")}>
                <span
                  className={clsx(
                    "text-3xl",
                    "font-bold",
                    "leading-none",
                    isSelected ? "text-primary" : "text-800"
                  )}
                >
                  {option.value}
                </span>
                <span className={clsx("flex", "min-w-0", "flex-col", "gap-1")}>
                  <span
                    className={clsx(
                      "text-sm",
                      "font-semibold",
                      "leading-snug",
                      "text-800",
                      "whitespace-normal",
                      "break-words"
                    )}
                  >
                    {option.label}
                  </span>
                  {option.description ? (
                    <span
                      className={clsx(
                        "text-xs",
                        "leading-snug",
                        "text-500",
                        "whitespace-normal",
                        "break-words"
                      )}
                    >
                      {option.description}
                    </span>
                  ) : null}
                </span>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
